import { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { DateTime } from 'luxon';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import {
    getClientScopeOfAppointmentStatus,
    getClientScopeOfAppointment,
    acceptScopeOfAppointment,
} from 'api/myClients';
import MuiButton from 'components/Common/Button/MuiButton';
import FormHOC from 'components/Common/Form/FormHOC';
import { PageLoadingCircle } from 'components/Common/Loading';
import { FEATURE_IDS } from 'constants/identifiers';
import { useConfigContext } from 'contexts/ConfigProvider';
import PageHeader from './Common/PageHeader';
import AuthSignatureForm from './ScopeOfAppointment/AuthSignatureForm';
import {
    FeedbackUnavailable,
    FeedbackCompleted,
    FeedbackCancelled,
} from './ScopeOfAppointment/FeedbackView';
import PlanCoverageDisclosureOptions from './ScopeOfAppointment/PlanCoverageDisclosureOptions';

const STATUS = {
    INACTIVE: 1,
    PENDING: 2,
    ACCEPTED: 3,
    EXPIRED: 4,
};

const soaFormSchema = yup.object({
    approveHmo: yup.boolean(),
    approvePpo: yup.boolean(),
    approvePos: yup.boolean(),
    approveSnp: yup.boolean(),
    hasInitial: yup
        .string()
        .when(['approvePpo', 'approvePos', 'approveSnp', 'approveHmo'], {
            is: (approvePpo, approvePos, approveSnp, approveHmo) =>
                !(approvePpo || approvePos || approveSnp || approveHmo),
            then: (schema) =>
                schema.required('At least 1 Plan Type should be selected.'),
        }),
    signature: yup.string().trim().required('This field is required'),
    signatureDate: yup.date().required('This field is required'),
});

const ScopeOfAppointmentDetails = ({ details }) => {
    const formatDate = (value: string) => {
        return DateTime.fromISO(value).toFormat('MM/dd/yyyy');
    };

    const sections = [
        {
            title: 'Client information',
            items: [
                [
                    'Client name',
                    `${details?.memberFirstName} ${details?.memberLastName}`,
                ],
                ['Medicare ID', details?.memberHic],
                ['Date of birth', formatDate(details?.memberDateOfBirth)],
                ['Email', details?.memberEmail],
                ['Phone', details?.memberPhoneNumber],
            ],
        },
        {
            title: 'Authorized Representative (If Applicable)',
            items: [
                ['Name', details?.authRepName],
                ['Relationship to Beneficiary', details?.authRelationship],
            ],
        },
        {
            title: 'Contact Info',
            items: [
                ['Email', details?.memberEmail],
                ['Phone', details?.memberPhoneNumber],
            ],
        },
        {
            title: 'Appointment details',
            items: [
                ['Agent name', details?.agentName],
                ['Agent phone', details?.agentPhone],
                ['Beneficiary name', details?.beneficiaryName],
                ['Beneficiary phone', details?.beneficiaryPhone],
                ['Beneficiary address', details?.beneficiaryAddress],
                ['Method of contact', details?.initialMethodContact],
                ['Represented plans', details?.representedPlans],
                ['Agent signature', details?.agentSignature],
                ['Appointment date', formatDate(details?.agentAppointmentDate)],
            ],
        },
    ];

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                mb: 3,
            }}
        >
            <Box
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    backgroundColor: 'grey.50',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Scope of appointment details
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    Please review the information below before signing.
                </Typography>
            </Box>
            {sections.map((section) => (
                <Box
                    key={section.title}
                    sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: 1.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 700,
                        }}
                    >
                        {section.title}
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.25,
                        }}
                    >
                        {section.items.map(([label, value]) => (
                            <Box key={label}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}

            <Box
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 1,
                        color: 'text.secondary',
                        fontWeight: 700,
                    }}
                >
                    Additional Notes
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                    }}
                >
                    {details?.briefExplanation ?? 'No notes provided.'}
                </Typography>
            </Box>
        </Box>
    );
};

const ScopeOfAppointmentReviewForm = ({
    details,
    onComplete,
}: {
    details: any;
    onComplete: () => void;
}) => {
    const { handleSubmit } = useFormContext();
    const [isLoading, setIsLoading] = useState(false);

    const onSubmit = (data) => {
        setIsLoading(true);
        acceptScopeOfAppointment({
            clientId: details.clientId,
            ...data,
        })
            .then(() => {
                onComplete();
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <PageHeader />
            <Box sx={{ m: 5, mt: 13, width: '100%', maxWidth: '1000px' }}>
                <ScopeOfAppointmentDetails details={details} />
                <PlanCoverageDisclosureOptions />
                <AuthSignatureForm />
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <MuiButton
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading}
                    >
                        Submit Signature
                    </MuiButton>
                </Box>
            </Box>
        </Box>
    );
};

const ScopeOfAppointmentReview = () => {
    const { featureFlags } = useConfigContext();
    const { clientId } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [scopeOfAppointment, setScopeOfAppointment] = useState<any>({});
    const [status, setStatus] = useState<number>();

    useEffect(() => {
        setIsLoading(true);
        getClientScopeOfAppointmentStatus(clientId ?? '')
            .then((status: number) => {
                setStatus(status ?? STATUS.INACTIVE);

                if (status === STATUS.PENDING) {
                    getClientScopeOfAppointment(clientId)
                        .then((data) => {
                            setScopeOfAppointment(data);
                        })
                        .catch(() => {
                            setScopeOfAppointment({});
                        });
                }
            })
            .catch(() => setStatus(STATUS.INACTIVE))
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    if (!featureFlags[FEATURE_IDS.STORE_SCOPE_OF_APPOINTMENTS]) {
        return null;
    }

    if (isLoading) {
        return <PageLoadingCircle />;
    }

    if (status === STATUS.EXPIRED || status === STATUS.INACTIVE) {
        return <FeedbackUnavailable />;
    } else if (status === STATUS.ACCEPTED) {
        return <FeedbackCompleted />;
    } else if (status !== STATUS.PENDING) {
        return <FeedbackUnavailable />;
    }

    return (
        <FormHOC
            yupValidations={soaFormSchema}
            mode="onSubmit"
            formDefaultValues={{ signatureDate: DateTime.now() }}
        >
            <ScopeOfAppointmentReviewForm
                details={scopeOfAppointment}
                onComplete={() => setStatus(STATUS.ACCEPTED)}
            />
        </FormHOC>
    );
};

export default ScopeOfAppointmentReview;
